import React, { useEffect, useState } from 'react';
import {
  listBusinessesForAdmin,
  approveBusiness,
  rejectBusiness,
  sendRequestInfoMessage,
  pillClassForStatus,
  labelForStatus,
} from '../lib/businesses.js';
import './VerificationPage.css';

// ============================================================
// VerificationPage — Session 4 (Phase 2).
//
// First admin-write surface. Lists businesses with
// status === 'pending_verification' (newest first) and lets admin
// Approve / Reject / Request-info via the detail drawer.
//
// ACTIONS
// -------
// Approve — writes verified: true + verifiedAt + verifiedBy. Business
//           gets the ✓ badge on the consumer directory (already visible
//           there during pending_verification). Optimistically removes
//           from the queue.
//
// Reject  — opens a required-reason dialog. Writes status: 'rejected'
//           + rejectedReason + rejectedAt + rejectedBy. Business drops
//           from consumer directory (rejected not in public-read
//           allowlist). Hard dead-end — no re-submit flow per product
//           decision. Optimistically removes from the queue.
//
// Request — placeholder for the Aangan Connect Inbox integration that
//           doesn't exist yet. Click shows a toast; no writes. Ships as
//           a real action once Aangan Connect lands in a future session.
//
// FIRESTORE COST
// --------------
// Page mount:     25 reads
// Load more:      25 per click
// Approve:        1 write
// Reject:         1 write
// Post-action:    0 reads (optimistic remove from local state)
// Post-error:     0 reads (state restored from before-write snapshot)
// ============================================================

// Format Firestore Timestamp / Date / ISO string for display.
function fmtDate(ts) {
  if (!ts) return '—';
  let d;
  if (typeof ts?.toDate === 'function') d = ts.toDate();
  else if (ts instanceof Date) d = ts;
  else if (typeof ts === 'string') d = new Date(ts);
  else if (typeof ts?.seconds === 'number') d = new Date(ts.seconds * 1000);
  else return '—';
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Generic value renderer for the detail drawer. Same shape as
// BusinessesPage / UsersPage drawers.
function renderFieldValue(v) {
  if (v == null || v === '') return <span className="sa-detail-empty">—</span>;
  if (typeof v?.toDate === 'function') return fmtDate(v);
  if (typeof v?.seconds === 'number' && typeof v?.nanoseconds === 'number') return fmtDate(v);
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (Array.isArray(v)) return v.length ? v.join(', ') : <span className="sa-detail-empty">—</span>;
  if (typeof v === 'object') return <pre className="sa-detail-json">{JSON.stringify(v, null, 2)}</pre>;
  if (typeof v === 'string' && v.startsWith('http')) {
    return (
      <a href={v} target="_blank" rel="noopener noreferrer" className="sa-detail-link">
        {v.length > 60 ? v.slice(0, 60) + '…' : v}
      </a>
    );
  }
  return String(v);
}

// Ordering for the drawer — verification-relevant fields near the top.
const DETAIL_TOP_FIELDS = [
  'name', 'slug', 'status', 'category', 'city', 'ownerId', 'phone',
  'email', 'shortDescription', 'description', 'addressLine1', 'addressLine2',
  'locality', 'pinCode', 'state', 'fssaiNumber', 'fssaiCertUrl',
  'fssaiVerified', 'tags', 'logo', 'createdAt',
];

// Session 5b: quick-pick templates for the Request Info confirm block.
// Sentence case + warm + plain-spoken per Aangan copy conventions.
// Each template's `message` is what gets sent verbatim if admin doesn't
// edit it. The 'custom' template's empty message forces admin to type
// something — validation on Send button requires non-empty text.
//
// New templates land as inline edits here; no config-collection UI
// this session. Reordering these changes the chip order in the UI.
const REQUEST_INFO_TEMPLATES = [
  {
    id: 'address_proof',
    label: 'Address proof',
    message: "Hi, thanks for registering your business with Aangan. To verify your business address, could you please share a photo or PDF of a recent utility bill, rent agreement, or municipal document showing the address? Thanks!",
  },
  {
    id: 'fssai_cert',
    label: 'FSSAI cert',
    message: "Hi, we couldn't verify your FSSAI license from the certificate uploaded. Could you please re-upload a clear photo of the license so the number and expiry date are readable? Thanks!",
  },
  {
    id: 'business_photos',
    label: 'Business photos',
    message: "Hi, thanks for registering. To help customers find and trust your business, could you please share 2-3 photos of your storefront or workspace here? Thanks!",
  },
  {
    id: 'custom',
    label: 'Custom message',
    message: '',
  },
];

export default function VerificationPage({ authUser }) {
  const [businesses, setBusinesses] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [selectedBiz, setSelectedBiz] = useState(null);
  const [toast, setToast] = useState(null);

  // Load first page on mount.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    listBusinessesForAdmin({ statusFilter: 'pending_verification', cursor: null })
      .then((res) => {
        if (cancelled) return;
        setBusinesses(res.businesses);
        setLastDoc(res.lastDoc);
        setHasMore(res.hasMore);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[VerificationPage] initial load failed:', err);
        setError(err?.message || 'Failed to load verification queue');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // Toast auto-dismisses.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore || !lastDoc) return;
    setLoadingMore(true);
    try {
      const res = await listBusinessesForAdmin({ statusFilter: 'pending_verification', cursor: lastDoc });
      setBusinesses((prev) => [...prev, ...res.businesses]);
      setLastDoc(res.lastDoc);
      setHasMore(res.hasMore);
    } catch (err) {
      console.error('[VerificationPage] load-more failed:', err);
      setError(err?.message || 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  // Optimistic remove — pulls the biz out of local state so the queue
  // updates instantly. Callers pass a revert function that restores the
  // biz if the underlying write fails.
  const optimisticRemove = (bizId) => {
    const snapshot = businesses;
    setBusinesses((prev) => prev.filter((b) => b.id !== bizId));
    setSelectedBiz(null);
    return () => setBusinesses(snapshot);
  };

  const handleApprove = async (biz) => {
    const revert = optimisticRemove(biz.id);
    try {
      await approveBusiness({ businessId: biz.id, adminUid: authUser?.uid });
      setToast({ kind: 'success', message: `${biz.name || 'Business'} approved.` });
    } catch (err) {
      console.error('[VerificationPage] approve failed:', err);
      revert();
      setToast({ kind: 'error', message: `Approve failed: ${err?.message || 'unknown error'}` });
    }
  };

  const handleReject = async (biz, reason) => {
    const revert = optimisticRemove(biz.id);
    try {
      await rejectBusiness({ businessId: biz.id, adminUid: authUser?.uid, reason });
      setToast({ kind: 'success', message: `${biz.name || 'Business'} rejected.` });
    } catch (err) {
      console.error('[VerificationPage] reject failed:', err);
      revert();
      setToast({ kind: 'error', message: `Reject failed: ${err?.message || 'unknown error'}` });
    }
  };

  const handleRequestInfo = async (biz, message) => {
    // Session 5b: sends a real support-chat message asking for more
    // information. Unlike Approve/Reject, this does NOT optimistically
    // remove the business from the queue — verification state is
    // unchanged. Admin can later Approve/Reject once the owner replies.
    // The drawer resets to 'idle' mode on success (VerificationDrawer's
    // internal state), letting admin either close manually or take
    // another action on the same business.
    try {
      await sendRequestInfoMessage({
        businessId: biz.id,
        ownerId: biz.ownerId,
        adminUid: authUser?.uid,
        message,
      });
      setToast({
        kind: 'success',
        message: `Request sent to ${biz.name || 'the business'} via Aangan Support.`,
      });
    } catch (err) {
      console.error('[VerificationPage] request info failed:', err);
      setToast({
        kind: 'error',
        message: `Request failed: ${err?.message || 'unknown error'}`,
      });
      // Rethrow so the drawer's doRequestInfo can keep the confirm block
      // open (admin can retry or Cancel — same UX as Reject on error).
      throw err;
    }
  };

  return (
    <>
      {error && (
        <div className="sa-error-banner">
          <b>Couldn't load verification queue.</b> {error}
        </div>
      )}

      <div className="sa-table-wrap">
        <table className="sa-table">
          <thead>
            <tr>
              <th>Business</th>
              <th>Category</th>
              <th>City</th>
              <th>Owner</th>
              <th>Phone</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="sa-table-empty">Loading…</td>
              </tr>
            )}

            {!loading && businesses.length === 0 && (
              <tr>
                <td colSpan={6} className="sa-table-empty">
                  No businesses awaiting verification. 🎉
                </td>
              </tr>
            )}

            {!loading && businesses.map((biz) => (
              <tr key={biz.id} onClick={() => setSelectedBiz(biz)}>
                <td>
                  <div className="sa-table-row-bizname">
                    <div className="sa-table-logo">{biz.logo || '🏪'}</div>
                    <div className="sa-table-bizname-text">
                      <div className="sa-table-bizname-primary">{biz.name || '—'}</div>
                      {biz.shortDescription && (
                        <div className="sa-table-bizname-secondary">{biz.shortDescription}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td>{biz.category || '—'}</td>
                <td>{biz.city || '—'}</td>
                <td className="sa-table-owner">{biz.ownerId || '—'}</td>
                <td>{biz.phone || <span className="sa-detail-empty">—</span>}</td>
                <td>{fmtDate(biz.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!loading && hasMore && (
        <div className="sa-load-more-wrap">
          <button className="sa-btn" onClick={handleLoadMore} disabled={loadingMore} type="button">
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      {selectedBiz && (
        <VerificationDrawer
          biz={selectedBiz}
          onClose={() => setSelectedBiz(null)}
          onApprove={() => handleApprove(selectedBiz)}
          onReject={(reason) => handleReject(selectedBiz, reason)}
          onRequestInfo={(message) => handleRequestInfo(selectedBiz, message)}
        />
      )}

      {toast && (
        <div className={`sa-toast sa-toast-${toast.kind}`}>
          {toast.message}
        </div>
      )}
    </>
  );
}

// ============================================================
// VerificationDrawer — read-through of the business doc plus the
// three action buttons at the bottom (Approve / Request info / Reject).
//
// Two-phase confirm for Approve and Reject:
//   - Approve: clicking Approve reveals inline confirm+cancel buttons
//   - Reject: clicking Reject reveals a textarea + confirm+cancel
// Neither uses window.confirm (jarring). Both let admin back out.
// ============================================================

function VerificationDrawer({ biz, onClose, onApprove, onReject, onRequestInfo }) {
  // 'idle' | 'confirming-approve' | 'confirming-reject' | 'confirming-request-info'
  const [mode, setMode] = useState('idle');
  const [rejectReason, setRejectReason] = useState('');
  const [requestInfoText, setRequestInfoText] = useState('');
  const [busy, setBusy] = useState(false);

  const allKeys = Object.keys(biz).filter((k) => k !== 'id');
  const topKeys = DETAIL_TOP_FIELDS.filter((k) => allKeys.includes(k));
  const restKeys = allKeys.filter((k) => !DETAIL_TOP_FIELDS.includes(k)).sort();
  const orderedKeys = [...topKeys, ...restKeys];

  const doApprove = async () => {
    setBusy(true);
    try { await onApprove(); } finally { setBusy(false); }
  };

  const doReject = async () => {
    if (!rejectReason.trim()) return;
    setBusy(true);
    try { await onReject(rejectReason); } finally { setBusy(false); }
  };

  const doRequestInfo = async () => {
    if (!requestInfoText.trim()) return;
    setBusy(true);
    try {
      await onRequestInfo(requestInfoText);
      // On success: reset the confirm block to idle so the drawer's
      // three action buttons come back. Business stays in the queue,
      // admin can close manually or take another action on the same biz.
      setMode('idle');
      setRequestInfoText('');
    } catch (_) {
      // Parent handler rethrows on failure so we can keep the confirm
      // block open (admin sees error toast + can retry or Cancel).
    } finally {
      setBusy(false);
    }
  };

  // Prefill the textarea when admin picks a template chip.
  const pickTemplate = (tpl) => {
    setRequestInfoText(tpl.message);
  };

  return (
    <>
      <div className="sa-drawer-backdrop" onClick={busy ? undefined : onClose} />
      <div className="sa-detail-panel open" role="dialog" aria-modal="true">
        <div className="sa-detail-panel-header">
          <div className="sa-detail-panel-title-block">
            <div className="sa-detail-panel-title">{biz.name || 'Business'}</div>
            <div className="sa-detail-panel-sub">
              <span className={`sa-pill ${pillClassForStatus(biz.status)}`}>
                {labelForStatus(biz.status)}
              </span>{' '}
              · ID {biz.id}
            </div>
          </div>
          <button
            className="sa-detail-panel-close"
            onClick={onClose}
            aria-label="Close"
            type="button"
            disabled={busy}
          >
            ×
          </button>
        </div>

        <div className="sa-detail-panel-body">
          <div className="sa-detail-block">
            <div className="sa-detail-block-title">All fields</div>
            {orderedKeys.map((key) => (
              <div className="sa-detail-row" key={key}>
                <div className="sa-detail-row-label">{key}</div>
                <div className="sa-detail-row-value">{renderFieldValue(biz[key])}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="sa-verification-actions">
          {mode === 'idle' && (
            <>
              <button
                className="sa-btn sa-btn-primary"
                onClick={() => setMode('confirming-approve')}
                type="button"
              >
                Approve & Start
              </button>
              <button
                className="sa-btn sa-btn-secondary"
                onClick={() => setMode('confirming-request-info')}
                type="button"
              >
                Request info
              </button>
              <button
                className="sa-btn sa-btn-danger"
                onClick={() => setMode('confirming-reject')}
                type="button"
              >
                Reject
              </button>
            </>
          )}

          {mode === 'confirming-approve' && (
            <div className="sa-confirm-block">
              <div className="sa-confirm-text">
                Approve <b>{biz.name}</b>? A ✓ badge will appear on the consumer directory.
              </div>
              <div className="sa-confirm-actions">
                <button
                  className="sa-btn sa-btn-secondary"
                  onClick={() => setMode('idle')}
                  type="button"
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  className="sa-btn sa-btn-primary"
                  onClick={doApprove}
                  type="button"
                  disabled={busy}
                >
                  {busy ? 'Approving…' : 'Confirm approve'}
                </button>
              </div>
            </div>
          )}

          {mode === 'confirming-reject' && (
            <div className="sa-confirm-block">
              <div className="sa-confirm-text">
                Reject <b>{biz.name}</b>? This is a hard dead-end — the owner cannot re-submit and will need to register a new business.
              </div>
              <textarea
                className="sa-reject-textarea"
                placeholder="Reason (required) — e.g. Fake business / Address unverifiable / Duplicate listing"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                disabled={busy}
                autoFocus
              />
              <div className="sa-confirm-actions">
                <button
                  className="sa-btn sa-btn-secondary"
                  onClick={() => { setMode('idle'); setRejectReason(''); }}
                  type="button"
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  className="sa-btn sa-btn-danger"
                  onClick={doReject}
                  type="button"
                  disabled={busy || !rejectReason.trim()}
                >
                  {busy ? 'Rejecting…' : 'Confirm reject'}
                </button>
              </div>
            </div>
          )}

          {mode === 'confirming-request-info' && (
            <div className="sa-confirm-block">
              <div className="sa-confirm-text">
                Send a request to <b>{biz.name}</b> via Aangan Support. The
                business owner will see it in their support chat. The
                business stays in the queue — you can Approve or Reject
                once they reply.
              </div>

              {/* Template chips — quick-pick common request types. */}
              <div className="sa-request-info-templates">
                {REQUEST_INFO_TEMPLATES.map((tpl) => (
                  <button
                    key={tpl.id}
                    type="button"
                    className="sa-request-info-chip"
                    onClick={() => pickTemplate(tpl)}
                    disabled={busy}
                  >
                    {tpl.label}
                  </button>
                ))}
              </div>

              <textarea
                className="sa-reject-textarea"
                placeholder="Write the request the owner should see, or pick a template above."
                value={requestInfoText}
                onChange={(e) => setRequestInfoText(e.target.value)}
                rows={5}
                disabled={busy}
                autoFocus
              />
              <div className="sa-confirm-actions">
                <button
                  className="sa-btn sa-btn-secondary"
                  onClick={() => { setMode('idle'); setRequestInfoText(''); }}
                  type="button"
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  className="sa-btn sa-btn-primary"
                  onClick={doRequestInfo}
                  type="button"
                  disabled={busy || !requestInfoText.trim()}
                >
                  {busy ? 'Sending…' : 'Send request'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
